import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { api, resolveSrc } from 'src/api/endpoints';
import SelectFilesModalScreen from 'src/views/screen/components/SelectFilesModalScreen';

const SLOTS = ['landscape', 'portrait'];

const PlaceholderBox = ({
  label,
  portrait,
  url,
  loading,
  disabled,
  onLibrary,
  onFile,
  onClear,
  resize,
}) => {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const inputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (disabled || loading) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) onFile(file);
  };

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        p: 2,
        border: '2px dashed',
        borderColor: dragOver ? 'primary.main' : 'divider',
        bgcolor: dragOver ? 'action.hover' : 'transparent',
        borderRadius: 2,
        transition: 'all 0.15s',
      }}
    >
      <Typography variant='subtitle2'>{label}</Typography>
      {url ? (
        <Box sx={{ position: 'relative' }}>
          <img
            src={resolveSrc(url)}
            alt={label}
            onClick={() => setPreviewOpen(true)}
            style={{
              display: 'block',
              maxWidth: '100%',
              width: 180,
              height: portrait ? 240 : 110,
              objectFit: resize || 'cover',
              borderRadius: 6,
              cursor: 'zoom-in',
              background: '#00000010',
            }}
          />
          {!disabled && (
            <IconButton
              size='small'
              onClick={onClear}
              sx={{
                position: 'absolute',
                top: -10,
                right: -10,
                bgcolor: 'background.paper',
              }}
            >
              <CloseIcon fontSize='small' />
            </IconButton>
          )}
          <Dialog
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            maxWidth='lg'
          >
            <img
              src={resolveSrc(url)}
              alt={label}
              style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
            />
          </Dialog>
        </Box>
      ) : (
        <Typography variant='caption' color='text.secondary' align='center'>
          {t('components.placeholderPicker.dropHere', 'Drop an image here')}
        </Typography>
      )}
      <Button
        size='small'
        variant='outlined'
        disabled={disabled || loading}
        onClick={onLibrary}
      >
        {t('components.placeholderPicker.addFromLibrary', 'Add from library')}
      </Button>
      <Button
        size='small'
        variant='text'
        disabled={disabled || loading}
        onClick={() => inputRef.current?.click()}
        sx={{ textDecoration: 'underline', textTransform: 'none' }}
      >
        {loading
          ? t('components.placeholderPicker.uploading', 'Uploading...')
          : t(
              'components.placeholderPicker.uploadFromComputer',
              'Upload from computer',
            )}
      </Button>
      <input
        ref={inputRef}
        type='file'
        accept='image/*'
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onFile(file);
        }}
      />
    </Box>
  );
};

PlaceholderBox.propTypes = {
  label: PropTypes.string,
  portrait: PropTypes.bool,
  url: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onLibrary: PropTypes.func,
  onFile: PropTypes.func,
  onClear: PropTypes.func,
  resize: PropTypes.oneOf(['cover', 'contain']),
};

export const PlaceholderPicker = ({
  value,
  onChange,
  disabled = false,
  only,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const hasImage = (only ? [only] : SLOTS).some((slot) => Boolean(value?.[slot]));
  useEffect(() => {
    if (hasImage) setOpen(true);
  }, [hasImage]);
  const [libraryTarget, setLibraryTarget] = useState(null);
  const [uploadingTarget, setUploadingTarget] = useState(null);

  const current = {
    landscape: value?.landscape || '',
    portrait: value?.portrait || '',
    resize: value?.resize === 'contain' ? 'contain' : 'cover',
  };

  const setSlot = (slot, url) => onChange({ ...current, [slot]: url });

  const handleFile = async (slot, file) => {
    if (!file.type?.startsWith('image/')) {
      toast.error(
        t(
          'components.placeholderPicker.onlyImages',
          'Only images are allowed as placeholder',
        ),
      );
      return;
    }
    setUploadingTarget(slot);
    try {
      const form = new FormData();
      form.append('content', file);
      const { data } = await api.post('/content/upload/placeholder', form);
      if (data?.content?.url) {
        setSlot(slot, data.content.url);
      }
    } catch (error) {
      const message = error?.response?.data;
      toast.error(
        typeof message === 'string' ? message : 'Error while uploading file',
      );
    } finally {
      setUploadingTarget(null);
    }
  };

  const labels = {
    landscape: t('components.placeholderPicker.landscape', 'Landscape'),
    portrait: t('components.placeholderPicker.portrait', 'Portrait'),
  };

  return (
    <Box>
      <Box
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          py: 1,
        }}
      >
        <Box>
          <Typography>
            {t('components.placeholderPicker.title', 'Placeholder')}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {t(
              'components.placeholderPicker.subtitle',
              'Set image displayed during playlist loading',
            )}
          </Typography>
        </Box>
        <Button
          variant='contained'
          color='primary'
          size='small'
          endIcon={
            <ExpandMore
              sx={{
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
          }
        >
          {t('components.placeholderPicker.edit', 'Edit')}
        </Button>
      </Box>
      <Collapse in={open}>
        <Box sx={{ display: 'flex', gap: 2, pt: 1, pb: 2 }}>
          {SLOTS.filter((slot) => !only || slot === only).map((slot) => (
            <PlaceholderBox
              key={slot}
              label={labels[slot]}
              portrait={slot === 'portrait'}
              url={current[slot]}
              loading={uploadingTarget === slot}
              disabled={disabled}
              onLibrary={() => setLibraryTarget(slot)}
              onFile={(file) => handleFile(slot, file)}
              onClear={() => setSlot(slot, '')}
              resize={current.resize}
            />
          ))}
        </Box>
        <Select
          size='small'
          fullWidth
          value={current.resize}
          disabled={disabled}
          onChange={(e) => onChange({ ...current, resize: e.target.value })}
          sx={{ mb: 2 }}
        >
          <MenuItem value='cover'>
            {t('components.placeholderPicker.zoom', 'Zoom')}
          </MenuItem>
          <MenuItem value='contain'>
            {t('components.placeholderPicker.fit', 'Fit to screen')}
          </MenuItem>
        </Select>
      </Collapse>
      {libraryTarget && (
        <SelectFilesModalScreen
          open
          imagesOnly
          setOpenSelectFiles={(isOpen) => {
            if (!isOpen) setLibraryTarget(null);
          }}
          onSaveFiles={() => {}}
          onSaveFilesForQuickP={(file) => {
            if (file?.type === 'image' && file?.url) {
              setSlot(libraryTarget, file.url);
            }
            setLibraryTarget(null);
          }}
        />
      )}
    </Box>
  );
};

PlaceholderPicker.propTypes = {
  value: PropTypes.shape({
    resize: PropTypes.oneOf(['cover', 'contain']),
    landscape: PropTypes.string,
    portrait: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  only: PropTypes.oneOf(['landscape', 'portrait']),
};

export default PlaceholderPicker;
